import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';

import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

// Composants
import { MessageComponent } from '../message/message.component';

// Utils
import { MassDeleteRefusal } from 'src/app/@shared/utilities/odata/mass-delete';
import { MassDeleteLabels } from './mass-delete-refusals.model';

/** Une ligne du tableau des refus, prête à afficher. */
interface RefusalRow {
    /** Le nom de l'objet à l'écran, ou son id quand il n'en porte plus. */
    name: string;
    /** Le ou les motifs du refus, rendus par le serveur. */
    reason: string;
}

@Component({
    selector: 'app-mass-delete-refusals',
    imports: [
        CommonModule,
        ButtonModule,
        TableModule,
        MessageComponent,
    ],
    templateUrl: './mass-delete-refusals.component.html'
})
export class MassDeleteRefusalsComponent {

    private _config = inject(DynamicDialogConfig);
    private _ref = inject(DynamicDialogRef);

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    public rows: RefusalRow[] = [];

    public title: string = '';

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    constructor() {
        const refusals: MassDeleteRefusal[] = this._config.data.refusals ?? [];
        const labels: MassDeleteLabels = this._config.data.labels;

        this.rows = refusals.map(refusal => this._toRow(refusal));
        this.title = this._buildTitle(refusals.length, labels);
    }

    public onClickClose(): void {
        this._close();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _buildTitle(count: number, labels: MassDeleteLabels): string {
        const noun = count > 1 ? labels.plural : labels.singular;
        const participle = `refusé${labels.feminine ? 'e' : ''}${count > 1 ? 's' : ''}`;
        return `${count} ${noun} ${participle}`;
    }

    private _close(): void {
        this._ref.close();
    }

    /**
     * Le motif tel que le serveur l'a rendu — le même qu'une suppression unitaire aurait
     * affiché. Un refus qu'aucune règle métier n'a nommé arrive avec un message vide :
     * plutôt qu'une cellule blanche, on dit que le motif manque, ce qui est signalable.
     */
    private _toRow(refusal: MassDeleteRefusal): RefusalRow {
        const reasons = refusal.messages
            .map(message => message.longText || message.shortText)
            .filter(text => !!text);

        return {
            name: refusal.label ?? `#${refusal.id}`,
            reason: reasons.length ? reasons.join(' ') : `Motif non précisé par Biifor.`
        };
    }
}
